import { useState } from 'react';
import { countryConfig } from '../country';
import { useT } from '../i18n/locale-context';
import { copyLink } from '../lib/copy-link';
import { needsExternalHandoff, openExternally, type InAppBrowser } from '../lib/in-app-browser';

/**
 * «Оплата надёжнее в браузере» — one small line under the Stripe pay button,
 * only inside an in-app browser (Instagram, Facebook, Telegram's own WebView).
 *
 * Why (kaztender.com, 15.10): a Kazakh buyer opened Stripe Checkout four times
 * in seven minutes from Instagram's browser on Android, and Stripe failed both
 * card attempts at the bank's 3-D Secure step — a step these WebViews are known
 * to break (the Uzbek hosted checkout measured 0 of 16 paid there, 19.09). Half
 * of Kazakhstan's Meta visitors arrive in Instagram's browser.
 *
 * It does NOT block anything: the button above still goes straight to Stripe.
 * Two ways out, where the platform allows them:
 *   - «Открыть в браузере» — the Android `intent://` hand-off the Uzbek
 *     checkout already uses (`openExternally`), for Instagram / Facebook on
 *     Android only, the same scope `needsExternalHandoff` keeps there;
 *   - «Скопировать ссылку» everywhere — iOS has no reliable way out of an
 *     in-app browser, so the address goes to the clipboard (`copyLink` copes
 *     with WebViews that refuse the Clipboard API).
 *
 * Stripe deployments only (`payments.checkout === 'stripe'`): Uzbekistan's
 * checkout has its own in-app handling and never renders this.
 *
 * `inApp` comes from the caller, which reads it AFTER mount (the prerendered
 * HTML has no user agent), so the first render is always empty.
 */
export function InAppPayHint({ inApp }: { inApp: InAppBrowser }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  if (countryConfig.payments.checkout !== 'stripe' || inApp.app === null) return null;

  const onCopy = () => {
    void copyLink(window.location.href)
      .then((ok) => {
        setCopied(ok);
        if (ok) window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setCopied(false));
  };

  const action =
    'inline-flex h-8 items-center rounded-md border-[1.5px] border-brand px-2.5 text-[12.5px] font-bold text-brand transition-colors hover:bg-brand-dim';

  return (
    <div
      data-testid="inapp-pay-hint"
      className="mt-2.5 rounded-md border border-edge bg-bone px-3 py-2.5 text-center"
    >
      <p className="text-[12.5px] leading-snug text-ink-soft">{t('plans.stripe.inAppHint')}</p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {needsExternalHandoff(inApp) && (
          <button
            type="button"
            className={action}
            onClick={() => {
              openExternally(window.location.href);
            }}
          >
            {t('plans.stripe.inAppOpen')}
          </button>
        )}
        <button type="button" className={action} onClick={onCopy}>
          {copied ? t('plans.handoffCopied') : t('plans.handoffCopy')}
        </button>
      </div>
    </div>
  );
}
